<template>
  <ProgramKeyIndicatorContainer
    :class="{
      'shadow-none': fullScreen
    }"
  >
    <div class="flex flex-col flex-1 min-h-0">
      <div v-if="!fullScreen" class="text-sm flex items-center justify-between">
        <span>{{ data.label }}</span>
      </div>
      <div class="relative flex-1 min-h-0">
        <div :style="{ minWidth: fullScreen ? chartMinWidth : 0, height: '100%' }">
          <BarChart
            :id="data.label"
            :data="chartData"
            :plugins="[ChartDataLabels as any]"
            :options="fullScreen ? fullScreenOptions : previewOptions"
          />
        </div>
      </div>
      <div class="text-sm">
        {{ data.extra }}
      </div>
    </div>
  </ProgramKeyIndicatorContainer>
</template>

<script lang="ts" setup>
import {computed} from "vue";
import {Bar as BarChart} from 'vue-chartjs';
import {
    Chart as ChartJS,
    Title,
    Tooltip,
    Filler,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    ArcElement,
    ChartData
} from 'chart.js';
import type {ChartOptions} from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartService from "@/laboratory/services/ChartService";
import {BarChartMarketStatistic} from "@/program/interfaces/Program";
import ProgramKeyIndicatorContainer from "@/program/components/program-statistics/ProgramKeyIndicatorContainer.vue";
import ProgramKeyIndicatorOptions from "@/program/services/ProgramKeyIndicatorOptions";

ChartJS.register(Title, Tooltip, Legend, Filler, BarElement, CategoryScale, LinearScale, ArcElement);

interface BarChartData {
    /** The label of bar */
    label: string;
    /** The value of the bar */
    value: number;
}

const {fullScreen = false, data} = defineProps<{
    data: BarChartMarketStatistic;
    fullScreen?: boolean;
}>();

/**
 * The chart data.
 */
const chartData = computed((): ChartData<"bar", (number | [number, number] | null)[]> => {
    const colors = ChartService.colorPalette();
    const datasets = Object.entries(data.value) || [];
    const firstSet = Object.values(Object.values(data.value)[0] as BarChartData[]);

    return {
        labels: firstSet.map((item: BarChartData) => item.label),
        datasets: datasets.map(([key, dataset], index) => {
            return {
                label: key,
                data: Object.values(dataset).map((item) => item.value),
                backgroundColor: colors[index],
            };
        }) as []
    };
});

/**
 * Base chart options shared between preview and fullscreen.
 */
const baseChartOptions = computed(() => {
    return {
        ...ProgramKeyIndicatorOptions.chartDefaultOptions,
        scales: {
            y: {
                stacked: data.variant === "stacked",
                ticks: {
                    callback: (tickValue: string | number) => {
                        return tickValue + ' ' + data.unit;
                    },
                },
            },
        },
    };
});

/**
 * Preview chart options.
 */
const previewOptions = computed<ChartOptions<'bar'>>(() => {
    return {
        ...baseChartOptions.value,
        plugins: {
            legend: {
                display: true,
                ...ProgramKeyIndicatorOptions.chartPreviewOptions.plugins!.legend
            },
            datalabels: {
                color: "primary",
                anchor: "end",
                offset: -3,
                align: "top",
                font: {
                    size: 8
                },
                formatter: (value: number) => {
                    return value;
                }
            },
        },
        scales: {
            ...baseChartOptions.value.scales,
            x: {
                stacked: data.variant === "stacked",
                ticks: {
                    autoSkip: false,
                    font: {
                        size: 9,
                    },
                },
            },
        },
    };
});

/**
 * Fullscreen chart options.
 */
const fullScreenOptions = computed<ChartOptions<'bar'>>(() => {
    return {
        ...baseChartOptions.value,
        maintainAspectRatio: ProgramKeyIndicatorOptions.chartFullScreenOptions.maintainAspectRatio ?? false,
        plugins: {
            legend: {
                display: true,
                ...ProgramKeyIndicatorOptions.chartFullScreenOptions.plugins!.legend
            },
            datalabels: {
                color: "primary",
                anchor: "end",
                offset: -3,
                align: "top",
                font: {
                    size: 10
                },
                formatter: (value: number) => {
                    return value;
                }
            },
        },
        scales: {
            ...baseChartOptions.value.scales,
            x: {
                stacked: data.variant === "stacked",
                ticks: {
                    autoSkip: true,
                    font: {
                        size: 11,
                    },
                    maxRotation: 45,
                    minRotation: 0,
                },
            },
        },
    };
});


const dataPointCount = computed(() => {
    const dataset = Object.values(data.value)[0];

    return Array.isArray(dataset) ? dataset.length : 0;
});

const chartMinWidth = computed(() => {
    const points = dataPointCount.value;
    const minWidthPerPoint = 20;
    const minWidth = 400;

    return `${Math.max(points * minWidthPerPoint, minWidth)}px`;
});
</script>
